'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { saveSessionField } from '@/lib/sessionService';
import SourceBadge from '@/components/SourceBadge';



interface EntryItem {
  id: string;
  who: string;
  who_en: string;
  situation: string;
  pain: string;
  solution: string;
  model: string;
  score: number;
  confidence: string;
}

interface UsClone {
  name: string;
  url: string;
  mrr_est: string;
  description: string;
  adapt_items: string[];
  clone_feasibility?: string;
  why_successful?: string;
  tried_queries?: string[];
}

interface EntryData {
  entries: EntryItem[];
  us_clone: UsClone;
  industry: string;
}

const MOCK_INDUSTRY = { name: 'SME Accounting & Tax Filing' };

export default function EntryPointContent() {
  const router = useRouter();
  const [data, setData] = useState<EntryData | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState<Record<string, unknown>>({});
  const [industryObj, setIndustryObj] = useState<{ name: string } & Record<string, unknown>>(MOCK_INDUSTRY);

  // Load profile on mount
  useEffect(() => {
    fetch('/api/profile')
      .then(r => r.json())
      .then(data => setProfile(data || {}))
      .catch(() => {});
  }, []);

  const loadEntryPoints = useCallback(async (industry: { name: string } & Record<string, unknown>, prof: Record<string, unknown>) => {
    setIsLoading(true);
    setError('');

    // Check persistent cache first
    try {
      const cached = localStorage.getItem('on_entry_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        const now = Date.now();
        const expires = new Date(parsed.expires_at).getTime();
        const sameIndustry = parsed.industry_id === (industry?.id || industry?.name);

        if (now < expires && sameIndustry && parsed.data) {
          setData(parsed.data);
          const firstId = parsed.data.entries?.length > 0 ? parsed.data.entries[0].id : '';
          const storedEntry = typeof window !== 'undefined' ? localStorage.getItem('orbital_entry') : null;
          let restoredId = '';
          if (storedEntry) {
            try { restoredId = JSON.parse(storedEntry)?.id || ''; } catch { restoredId = storedEntry; }
          }
          setSelectedId(restoredId || firstId);
          setIsLoading(false);
          return;
          // DO NOT delete cache here
        }
        // Only delete if expired or wrong industry
        localStorage.removeItem('on_entry_cache');
      }
    } catch {
      localStorage.removeItem('on_entry_cache');
    }

    // No valid cache → call API
    try {
      const res = await fetch('/api/entry-point', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ industry, profile: prof }),
      });
      if (!res.ok) throw new Error('Failed to load entry points');
      const json = await res.json();
      setData(json);
      // Restore previously selected entry if it exists
      const storedEntry = typeof window !== 'undefined' ? localStorage.getItem('orbital_entry') : null;
      let restoredId = '';
      if (storedEntry) {
        try {
          const parsed = JSON.parse(storedEntry);
          restoredId = parsed?.id || '';
        } catch {
          restoredId = storedEntry;
        }
      }
      const firstId = json.entries?.length > 0 ? json.entries[0].id : '';
      setSelectedId(restoredId || firstId);

      // Save to persistent cache (24h TTL)
      if (json?.entries?.length > 0) {
        localStorage.setItem('on_entry_cache', JSON.stringify({
          data: json,
          industry_id: industry?.id || industry?.name,
          cached_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        }));

        // Auto-save to Supabase immediately when fresh entry data arrives
        const defaultEntry = json.entries[0];
        saveSessionField({
          industry: industry?.name || '',
          entry: defaultEntry,
          entry_who: defaultEntry?.who || null,
          screen_reached: 2,
        }).catch(() => {});
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Entry point generation failed';
      setError(msg);
      toast.error('Failed to load entry points — check connection and retry');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Read full industry object from on_industry_full
    const industryFull = typeof window !== 'undefined' ? localStorage.getItem('on_industry_full') : null;
    const industry = industryFull
      ? JSON.parse(industryFull)
      : { name: (typeof window !== 'undefined' ? localStorage.getItem('orbital_industry') : null) || MOCK_INDUSTRY.name };
    setIndustryObj(industry);
    loadEntryPoints(industry, profile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEntryPoints]);

  // Re-load when profile is fetched (only if industry already set and data not yet loaded)
  const [profileLoaded, setProfileLoaded] = useState(false);
  useEffect(() => {
    if (!profileLoaded && Object.keys(profile).length > 0) {
      setProfileLoaded(true);
      const industryFull = typeof window !== 'undefined' ? localStorage.getItem('on_industry_full') : null;
      const industry = industryFull
        ? JSON.parse(industryFull)
        : { name: (typeof window !== 'undefined' ? localStorage.getItem('orbital_industry') : null) || MOCK_INDUSTRY.name };
      setIndustryObj(industry);
      loadEntryPoints(industry, profile);
    }
  }, [profile, profileLoaded, loadEntryPoints]);

  // Save selected entry to on_entry on browser close/crash
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (typeof window === 'undefined' || !data || !selectedId) return;
      const selectedEntry = (data.entries ?? []).find(e => e.id === selectedId);
      if (selectedEntry) {
        localStorage.setItem('on_entry', JSON.stringify(selectedEntry));
        localStorage.setItem('orbital_entry', JSON.stringify(selectedEntry));
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [data, selectedId]);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    // Save full entry object to localStorage on card selection
    if (typeof window !== 'undefined' && data) {
      const selectedEntry = data.entries.find(e => e.id === id);
      if (selectedEntry) {
        localStorage.setItem('orbital_entry', JSON.stringify(selectedEntry));
        localStorage.setItem('on_entry', JSON.stringify(selectedEntry));
      }
    }
  };

  const handleNext = () => {
    if (!selectedId) return;
    if (typeof window !== 'undefined') {
      const selectedEntry = data?.entries?.find(e => e.id === selectedId);
      if (selectedEntry) {
        localStorage.setItem('orbital_entry', JSON.stringify(selectedEntry));
        localStorage.setItem('on_entry', JSON.stringify(selectedEntry));
      } else {
        localStorage.setItem('orbital_entry', selectedId);
        localStorage.setItem('on_entry', selectedId);
      }
    }
    const selectedEntry = data?.entries?.find(e => e.id === selectedId);
    if (selectedEntry) {
      saveSessionField({
        industry: industryObj.name || '',
        entry: selectedEntry,
        entry_who: selectedEntry?.who || null,
        screen_reached: 2,
      }).catch(() => {});
    }
    router.push('/market-sizing');
  };

  if (isLoading) return <EntryPointSkeleton />;

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <span className="font-mono text-red text-[13px]">{error}</span>
        <button
          onClick={() => loadEntryPoints(industryObj, profile)}
          className="btn-primary px-6 py-2 font-mono text-[12px] tracking-wider"
        >
          RETRY
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '40px 0',
        fontFamily: 'IBM Plex Mono',
        fontSize: '12px',
        color: '#6b6b80'
      }}>
        <div style={{
          width: '6px', height: '6px',
          borderRadius: '50%',
          background: '#00ff88',
          animation: 'pulse 1.5s infinite'
        }}/>
        Generating entry points...
      </div>
    );
  }

  const entries = data?.entries ?? [];
  const usClone = data?.us_clone ?? null;

  if (entries.length === 0) {
    return (
      <div style={{
        padding: '20px',
        fontFamily: 'IBM Plex Mono',
        fontSize: '12px',
        color: '#6b6b80',
        textAlign: 'center'
      }}>
        No entry points generated.
        <br/>
        <button onClick={() => loadEntryPoints(industryObj, profile)}>Try again →</button>
      </div>
    );
  }

  const industryName = industryObj.name || data.industry || '';

  // Determine US clone state
  const usCloneState: 'found' | 'gap' | 'unverified' = (() => {
    if (!usClone || usClone.name === 'none' || usClone.name === 'no equivalent found') return 'gap';
    if (!usClone.name || usClone.name === 'not verified' || usClone.name === 'not verified, manual check needed' || (usClone as unknown as Record<string, unknown>).error) return 'unverified';
    return 'found';
  })();

  return (
    <div className="fade-in flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-1">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-1">
          <button
            onClick={() => router.push('/discovery')}
            className="font-mono text-[11px] text-text3 hover:text-text2 transition-colors"
          >
            Discovery
          </button>
          <span className="font-mono text-[11px] text-text3">→</span>
          <span className="font-mono text-[11px] text-text2">{industryName}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] text-text2 tracking-[3px] uppercase">Layer 2</span>
        </div>
        <h1 className="text-[22px] font-semibold text-text" style={{ fontFamily: 'var(--font-dm-sans)' }}>
          Entry Point Builder
        </h1>
        <p className="text-[14px] text-text2">WHO + SITUATION + Pain + Solution concept.</p>
      </div>

      {/* Entry cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {entries.map((entry, index) => {
          const isSelected = entry.id === selectedId;
          const cardNum = String(index + 1).padStart(2, '0');
          const scoreFormatted = entry.score >= 10 ? (entry.score / 10).toFixed(1) : entry.score.toFixed(1);
          return (
            <button
              key={entry.id}
              onClick={() => handleSelect(entry.id)}
              className="text-left transition-all duration-200 rounded-[8px] p-5 flex flex-col gap-4"
              style={{
                backgroundColor: isSelected ? 'rgba(57,255,110,0.04)' : 'var(--surface)',
                border: isSelected ? '1px solid rgba(57,255,110,0.4)' : '1px solid var(--border)',
                boxShadow: isSelected ? '0 0 0 1px rgba(57,255,110,0.15), 0 4px 24px rgba(57,255,110,0.06)' : 'none',
                outline: 'none',
              }}
            >
              {/* Card header: ENTRY POINT 01 — Score: X.X/10 */}
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[11px] text-text3 tracking-wider uppercase">
                  ENTRY POINT {cardNum} — Score: {scoreFormatted}/10
                </span>
              </div>

              {/* WHO */}
              <div className="flex flex-col gap-1">
                <span className="font-mono text-[10px] text-text3 tracking-wider uppercase">WHO</span>
                <span className="text-[15px] font-semibold text-text leading-snug">{entry.who}</span>
                <span className="text-[12px] text-text2">{entry.who_en}</span>
              </div>

              {/* Situation */}
              <div className="flex flex-col gap-1">
                <span className="font-mono text-[10px] text-text3 tracking-wider uppercase">SITUATION</span>
                <p className="text-[13px] text-text2 leading-relaxed">{entry.situation}</p>
              </div>

              {/* Pain box */}
              <div
                className="rounded-[6px] p-3"
                style={{ backgroundColor: 'rgba(255,61,61,0.06)', border: '1px solid rgba(255,61,61,0.15)' }}
              >
                <span className="font-mono text-[10px] tracking-wider" style={{ color: 'var(--red)' }}>PAIN</span>
                <p className="text-[13px] text-text2 mt-1 leading-relaxed">{entry.pain}</p>
              </div>

              {/* Solution */}
              <div
                className="rounded-[6px] p-3"
                style={{ backgroundColor: 'var(--green-dim)', border: '1px solid rgba(57,255,110,0.15)' }}
              >
                <span className="font-mono text-[10px] text-green tracking-wider">SOLUTION</span>
                <p className="text-[13px] text-text2 mt-1 leading-relaxed">{entry.solution}</p>
              </div>

              {/* Footer: monetization chip + score */}
              <div className="flex items-center justify-between">
                <span className="tag-mono">{entry.model}</span>
                <span
                  className="font-mono text-[10px] tracking-wider px-2 py-1 rounded"
                  style={{
                    color: entry.confidence === 'HIGH' ? 'var(--green)' : 'var(--yellow)',
                    backgroundColor: entry.confidence === 'HIGH' ? 'var(--green-dim)' : 'rgba(255,210,63,0.08)',
                    border: `1px solid ${entry.confidence === 'HIGH' ? 'rgba(57,255,110,0.25)' : 'rgba(255,210,63,0.25)'}`,
                  }}
                >
                  {entry.confidence} CONFIDENCE
                </span>
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full pulse-dot" style={{ backgroundColor: 'var(--green)' }} />
                  <span className="font-mono text-[11px] text-green">SELECTED</span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* US Benchmark strip */}
      {usCloneState === 'found' && usClone && (
        <div
          className="rounded-[8px] p-5"
          style={{ backgroundColor: 'var(--surface2)', border: '1px solid var(--border)' }}
        >
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-[16px]">🇺🇸</span>
            <span className="font-mono text-[11px] text-text2 tracking-wider uppercase">US Benchmark</span>
            <span className="tag-mono">{usClone.name}</span>
            {usClone.url && (
              <a
                href={usClone.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[11px] text-text3 hover:text-text2 transition-colors underline underline-offset-2"
              >
                {usClone.url}
              </a>
            )}
            <span className="ml-auto tag-green">{usClone.mrr_est}</span>
            {usClone.clone_feasibility && (
              <span
                className="font-mono text-[10px] tracking-wider px-2 py-1 rounded"
                style={{
                  color: usClone.clone_feasibility === 'HIGH' ? 'var(--green)'
                    : usClone.clone_feasibility === 'MED' || usClone.clone_feasibility === 'MEDIUM' ? 'var(--yellow)' : 'var(--red)',
                  backgroundColor: usClone.clone_feasibility === 'HIGH' ? 'var(--green-dim)'
                    : usClone.clone_feasibility === 'MED' || usClone.clone_feasibility === 'MEDIUM' ? 'rgba(255,210,63,0.08)' : 'rgba(255,61,61,0.08)',
                  border: `1px solid ${usClone.clone_feasibility === 'HIGH' ? 'rgba(57,255,110,0.25)'
                    : usClone.clone_feasibility === 'MED' || usClone.clone_feasibility === 'MEDIUM' ? 'rgba(255,210,63,0.25)' : 'rgba(255,61,61,0.25)'}`,
                }}
              >
                {usClone.clone_feasibility} CLONE
              </span>
            )}
            {(() => {
              const cloneBadgeSource =
                !usClone?.name ||
                usClone.name === 'none' ||
                usClone.name === 'inconclusive' ?'HUMAN' :'PERPLEXITY';
              return <SourceBadge source={cloneBadgeSource} />;
            })()}
          </div>
          {(() => {
            const shortDesc = usClone?.why_successful
              ?.replace(/\[Source:.*?\]/g, '')
              ?.split('.')
              .filter(s => s.trim().length > 0)
              .slice(0, 2)
              .join('.') + '.';
            return shortDesc && shortDesc !== '.' ? (
              <p className="text-[13px] text-text2 mb-3 leading-relaxed">{shortDesc}</p>
            ) : usClone.description ? (
              <p className="text-[13px] text-text2 mb-3 leading-relaxed">
                {usClone.description
                  .replace(/\[Source:.*?\]/g, '')
                  .split('.')
                  .filter((s: string) => s.trim().length > 0)
                  .slice(0, 2)
                  .join('.') + '.'}
              </p>
            ) : null;
          })()}
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[10px] text-text3 tracking-wider uppercase mb-1">Vietnam Adaptations</span>
            {(usClone.adapt_items ?? []).map((item, i) => (
              <div key={`adapt-${i + 1}`} className="flex items-start gap-2">
                <span className="font-mono text-[11px] text-green mt-[2px]">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-[13px] text-text2">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {usCloneState === 'gap' && (
        <div
          className="rounded-[8px] p-5 flex flex-col gap-2"
          style={{ backgroundColor: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.2)' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-[16px]">🇺🇸</span>
            <span className="font-mono text-[11px] text-text2 tracking-wider uppercase">US Benchmark</span>
          </div>
          <p className="text-[14px] font-semibold" style={{ color: '#00ff88' }}>
            🟢 No direct US equivalent found
          </p>
          <p className="text-[13px] text-text2 leading-relaxed">
            This may be a genuine gap opportunity — no established indie tool in the US market solves this exact problem.
          </p>
        </div>
      )}

      {usCloneState === 'unverified' && (
        <div
          className="rounded-[8px] p-5 flex flex-col gap-3"
          style={{ backgroundColor: 'rgba(255,165,0,0.06)', border: '1px solid rgba(255,165,0,0.2)' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-[16px]">🇺🇸</span>
            <span className="font-mono text-[11px] text-text2 tracking-wider uppercase">US Benchmark</span>
          </div>
          <p className="text-[14px] font-semibold" style={{ color: 'rgba(255,165,0,0.9)' }}>
            🔍 US market search inconclusive
          </p>
          {usClone?.tried_queries && usClone.tried_queries.length > 0 && (
            <p className="text-[12px] font-mono" style={{ color: 'rgba(255,165,0,0.7)' }}>
              Searched: {usClone.tried_queries.map(q => `'${q}'`).join(', ')} — No direct US indie tool found.
              This may be a genuine gap opportunity.
            </p>
          )}
          <p className="text-[13px] text-text2 leading-relaxed">
            Recommended: manually check these sources for <span className="font-mono text-[12px]">&quot;{industryName}&quot;</span> tools:
          </p>
          <div className="flex flex-col gap-2 mt-1">
            <a
              href={`https://www.producthunt.com/search?q=${encodeURIComponent(industryName)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[12px] transition-colors"
              style={{ color: 'rgba(255,165,0,0.85)' }}
            >
              Search ProductHunt →
            </a>
            <a
              href={`https://www.indiehackers.com/products?category=${encodeURIComponent(industryName)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[12px] transition-colors"
              style={{ color: 'rgba(255,165,0,0.85)' }}
            >
              Search IndieHackers →
            </a>
            <a
              href={`https://alternativeto.net/browse/search/?q=${encodeURIComponent(industryName)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[12px] transition-colors"
              style={{ color: 'rgba(255,165,0,0.85)' }}
            >
              Search AlternativeTo →
            </a>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={() => router.push('/discovery')}
          className="btn-ghost px-5 py-2 text-[14px]"
        >
          ← Back
        </button>
        <button
          onClick={handleNext}
          disabled={!selectedId}
          className="btn-primary px-6 py-2 text-[14px] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Size the Market →
        </button>
      </div>
    </div>
  );
}

function EntryPointSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="animate-pulse bg-surface2 rounded h-4 w-48" />
        <div className="animate-pulse bg-surface2 rounded h-7 w-72" />
        <div className="animate-pulse bg-surface2 rounded h-4 w-56" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {['skel-entry-1', 'skel-entry-2'].map(k => (
          <div key={k} className="card-surface p-5 flex flex-col gap-4">
            <div className="animate-pulse bg-surface2 rounded h-6 w-3/4" />
            <div className="animate-pulse bg-surface2 rounded h-16 w-full" />
            <div className="animate-pulse bg-surface2 rounded h-12 w-full" />
            <div className="animate-pulse bg-surface2 rounded h-12 w-full" />
          </div>
        ))}
      </div>
      <div className="card-surface2 p-5">
        <div className="animate-pulse bg-surface rounded h-24 w-full" />
      </div>
    </div>
  );
}